import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { ADMIN_ROLE_ID, FRONT_DESK_ROLE_ID } from '@/lib/roles';

export default function SetPasswordPage() {
  const navigate = useNavigate();
  const { session, profile, loading: authLoading } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [hasValidSession, setHasValidSession] = useState<boolean | null>(null);

  useEffect(() => {
    if (authLoading) return;

    if (!session) {
      setHasValidSession(false);
      return;
    }

    setHasValidSession(true);
  }, [session, authLoading]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;

    setError(null);

    if (password.length < 8) {
      setError('パスワードは8文字以上で入力してください');
      return;
    }

    if (password !== confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }

    setSubmitting(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });

      if (updateError) {
        setError('パスワードの設定に失敗しました。再度お試しください。');
        setSubmitting(false);
        return;
      }

      setSuccess(true);

      // Redirect after short delay based on role
      setTimeout(() => {
        if (profile) {
          if (profile.role_id === ADMIN_ROLE_ID) {
            navigate('/', { replace: true });
          } else if (profile.role_id === FRONT_DESK_ROLE_ID) {
            navigate('/reservations', { replace: true });
          } else {
            navigate('/cleaning', { replace: true });
          }
        }
      }, 2000);
    } catch {
      setError('予期せぬエラーが発生しました');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <span className="w-10 h-10 flex items-center justify-center mx-auto mb-4">
            <i className="ri-loader-4-line text-3xl text-primary-500 animate-spin"></i>
          </span>
          <p className="text-sm text-foreground-600">確認中...</p>
        </div>
      </div>
    );
  }

  // Invalid session
  if (hasValidSession === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 px-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
            <span className="w-8 h-8 flex items-center justify-center">
              <i className="ri-close-circle-line text-3xl text-red-500"></i>
            </span>
          </div>
          <h1 className="text-lg font-bold text-foreground-900 font-heading mb-2">
            招待リンクが無効または期限切れです
          </h1>
          <p className="text-sm text-foreground-600">
            新しい招待リンクが必要な場合は、管理者にお問い合わせください。
          </p>
          <a
            href="/login"
            className="inline-flex items-center gap-2 mt-6 px-4 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-login-box-line text-sm"></i>
            </span>
            ログイン画面へ
          </a>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 px-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
            <span className="w-8 h-8 flex items-center justify-center">
              <i className="ri-check-line text-3xl text-accent-700"></i>
            </span>
          </div>
          <h1 className="text-lg font-bold text-foreground-900 font-heading mb-2">
            パスワードを設定しました
          </h1>
          <p className="text-sm text-foreground-600">
            画面を切り替えます...
          </p>
        </div>
      </div>
    );
  }

  // Password form
  return (
    <div className="min-h-screen flex items-center justify-center bg-background-50 px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img
            src="/assets/peche-et-fleur-logo.jpg"
            alt="Peche et Fleur"
            className="w-52 h-44 object-contain mx-auto mb-4"
          />
          <h1 className="text-xl font-bold text-foreground-900 font-heading">Peche et Fleur Staff Portal</h1>
          <p className="text-sm text-foreground-600 mt-1">アカウント設定</p>
        </div>

        <form
          noValidate
          onSubmit={handleSubmit}
          className="bg-background-50 rounded-xl border border-background-200/70 p-6 space-y-4"
        >
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-foreground-800 mb-1.5">
              新しいパスワード
            </label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 transition-colors"
              placeholder="8文字以上"
            />
            <p className="text-xs text-foreground-500 mt-1">8文字以上で入力してください</p>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground-800 mb-1.5">
              パスワード（確認）
            </label>
            <input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 transition-colors"
              placeholder="もう一度入力"
            />
          </div>

          {error && (
            <div className="flex items-start gap-2.5 rounded-lg bg-red-50 px-3.5 py-2.5">
              <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-px">
                <i className="ri-error-warning-line text-sm text-red-600"></i>
              </span>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-loader-4-line text-sm animate-spin"></i>
                </span>
                設定中...
              </>
            ) : (
              <>
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-lock-line text-sm"></i>
                </span>
                パスワードを設定
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
